import { useEffect, useMemo, useState } from "react";
import { CalendarRange, X } from "lucide-react";
import { MONTHS } from "@/lib/format";

const pad = (n) => String(n).padStart(2, "0");
const lastDayOf = (y, m) => new Date(y, m, 0).getDate();

/**
 * Seletor de período padronizado: mês/ano + 1ª quinzena / 2ª quinzena / Mensal.
 * Emite start/end (YYYY-MM-DD) compatível com inRange e com os params year/month.
 * - monthlyOnly: mantém os 3 botões visíveis, porém o período sempre cobre o mês inteiro.
 * - clearable: exibe botão "Limpar" que zera o período (active=null -> start/end vazios).
 */
export function PeriodSelector({
  start,
  end,
  setStart,
  setEnd,
  testPrefix = "period",
  label = "Período",
  monthlyOnly = false,
  clearable = false,
  defaultActive = "mes", // "mes" | "1" | "2" | null
}) {
  const now = new Date();
  const curMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const [month, setMonth] = useState(curMonth);
  const [active, setActive] = useState(defaultActive);

  const months = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      arr.push({ value: `${y}-${pad(m)}`, label: `${MONTHS[m - 1]} de ${y}` });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (active === null) {
      setStart("");
      setEnd("");
      return;
    }
    const [y, m] = month.split("-").map(Number);
    const ld = lastDayOf(y, m);
    let s;
    let e;
    if (monthlyOnly || active === "mes") {
      s = `${month}-01`;
      e = `${month}-${pad(ld)}`;
    } else if (active === "1") {
      s = `${month}-01`;
      e = `${month}-15`;
    } else {
      s = `${month}-16`;
      e = `${month}-${pad(ld)}`;
    }
    setStart(s);
    setEnd(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, active, monthlyOnly]);

  const btns = [
    { k: "1", label: "1ª quinzena" },
    { k: "2", label: "2ª quinzena" },
    { k: "mes", label: "Mensal" },
  ];

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 p-3"
      data-testid={`${testPrefix}-filter`}
    >
      <div className="flex items-center gap-1.5 text-slate-300">
        <CalendarRange className="h-4 w-4 text-orange-400" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <select
        value={month}
        onChange={(e) => {
          setMonth(e.target.value);
          setActive((a) => (a === null ? "mes" : a));
        }}
        data-testid={`${testPrefix}-month`}
        className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100"
      >
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <div className="flex overflow-hidden rounded-lg border border-slate-700">
        {btns.map((b) => (
          <button
            key={b.k}
            onClick={() => setActive(b.k)}
            data-testid={`${testPrefix}-${b.k === "mes" ? "mensal" : `q${b.k}`}`}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              active === b.k
                ? "bg-orange-500 text-white"
                : "bg-slate-900 text-slate-300 hover:bg-slate-800"
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>
      {clearable && active !== null && (
        <button
          onClick={() => setActive(null)}
          data-testid={`${testPrefix}-clear`}
          className="flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-sm text-slate-400 hover:border-rose-500/40 hover:text-rose-300"
        >
          <X className="h-3.5 w-3.5" /> Limpar
        </button>
      )}
    </div>
  );
}
